<template>
  <div class="flex flex-col gap-5">
    <section
      class="rounded-lg border border-slate-200 bg-white p-5 shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
    >
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <p class="text-[24px] font-black leading-8 text-slate-950">
            Câu hỏi thường gặp
          </p>
          <p
            class="mt-2 max-w-4xl text-[15px] font-medium leading-6 text-slate-600"
          >
            Tổng hợp các lỗi và thắc mắc thường gặp khi sử dụng hệ thống Zalo
            OA.
          </p>
        </div>
        <RouterLink
          to="/guide"
          class="inline-flex min-h-10 items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-extrabold !text-teal-800 !no-underline transition hover:bg-teal-100"
        >
          <i class="pi pi-book"></i>
          Xem hướng dẫn
        </RouterLink>
      </div>
    </section>

    <section class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div
        v-for="group in faqGroups"
        :key="group.title"
        class="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
      >
        <div class="border-b border-slate-200 bg-slate-50 px-4 py-4">
          <div class="flex items-center gap-2">
            <span
              class="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-100 text-teal-800"
            >
              <i class="pi" :class="group.icon"></i>
            </span>
            <p class="text-[17px] mb-0 font-black text-slate-950">
              {{ group.title }}
            </p>
          </div>
        </div>

        <div class="divide-y divide-slate-100">
          <details
            v-for="item in group.items"
            :key="item.question"
            class="group p-4"
          >
            <summary
              class="flex cursor-pointer list-none items-start justify-between gap-3 text-[15px] font-extrabold leading-6 text-slate-900"
            >
              <span>{{ item.question }}</span>
              <i
                class="pi pi-chevron-down ml-2 mt-1 text-xs text-slate-400 transition group-open:rotate-180"
              ></i>
            </summary>
            <p class="mt-3 font-medium leading-6 text-slate-600">
              {{ item.answer }}
            </p>
          </details>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
type FaqItem = {
  question: string;
  answer: string;
};

type FaqGroup = {
  title: string;
  icon: string;
  items: FaqItem[];
};

export default {
  name: "FAQPage",
  setup() {
    const faqGroups: FaqGroup[] = [
      {
        title: "Đăng nhập và phân quyền",
        icon: "pi-lock",
        items: [
          {
            question: "Tôi đăng nhập không được thì kiểm tra gì trước?",
            answer:
              "Kiểm tra tên đăng nhập, mật khẩu và trạng thái mạng. Nếu vẫn lỗi, liên hệ quản trị để kiểm tra tài khoản có bị sai quyền, khóa hoặc chưa được tạo đúng chưa.",
          },
          {
            question: "Vì sao tôi không thấy một số menu?",
            answer:
              "Menu được hiển thị theo quyền tài khoản. Tài khoản quản trị thấy nhiều chức năng hơn, tài khoản tổ/phường chỉ thấy các chức năng được cấp quyền.",
          },
          {
            question: "Tại sao hệ thống tự quay về màn hình đăng nhập?",
            answer:
              "Khi token hết hạn hoặc API trả về lỗi 401, hệ thống tự xóa thông tin phiên và yêu cầu đăng nhập lại.",
          },
        ],
      },
      {
        title: "Gửi tin nhắn và hình ảnh",
        icon: "pi-send",
        items: [
          {
            question:
              "Gửi tin nhắn cho từng khách hàng nhưng nút gửi không hoạt động?",
            answer:
              "Cần chọn phạm vi gửi, chọn ít nhất một khách hàng và nhập nội dung tin nhắn. Nếu thiếu một trong các thông tin này, hệ thống không cho gửi.",
          },
          {
            question: "Tin nhắn mẫu cần chuẩn bị gì?",
            answer:
              "Cần có tiêu đề, nội dung, ảnh chính, đường dẫn chính và các thành phần hành động nếu dùng. Ảnh nên được tải trước ở chức năng Đăng hình ảnh.",
          },
          {
            question: "Ảnh nên dùng định dạng và kích thước nào?",
            answer:
              "Nên dùng JPG, JPEG hoặc PNG. Ảnh khung chính khuyến nghị 360 x 232 px, ảnh thành phần khuyến nghị 50 x 50 px.",
          },
        ],
      },
      {
        title: "Dân cư và nhóm",
        icon: "pi-users",
        items: [
          {
            question: "Import dân cư bị lỗi thì xử lý thế nào?",
            answer:
              "Xem bảng Danh sách hồ sơ lỗi để biết dòng nào sai và thông báo lỗi cụ thể. Sau đó sửa lại file Excel theo mẫu rồi đăng lại.",
          },
          {
            question: "Xóa dân cư hoặc thành viên nhóm có cần xác nhận không?",
            answer:
              "Có. Các thao tác xóa đều mở hộp thoại xác nhận để tránh xóa nhầm.",
          },
          {
            question: "Tạo nhóm để làm gì?",
            answer:
              "Nhóm giúp gom nhiều người nhận vào một tập cố định, thuận tiện khi gửi tin nhắn theo nhóm.",
          },
        ],
      },
      {
        title: "Văn bản và góp ý",
        icon: "pi-file",
        items: [
          {
            question: "Gửi văn bản hỗ trợ tệp gì?",
            answer:
              "Chức năng gửi văn bản chỉ chấp nhận file PDF và có thể chọn nhiều PDF cùng lúc.",
          },
          {
            question: "Người nhận phản hồi văn bản ở đâu?",
            answer:
              "Người nhận vào Danh sách văn bản, mở chi tiết văn bản, xem/tải file đính kèm và nhập nội dung trả lời nếu văn bản chưa xử lý.",
          },
          {
            question: "Quản trị xem ai đã xử lý văn bản ở đâu?",
            answer:
              "Vào Quản lý văn bản, bấm Xem ở cột Chi tiết xử lý để xem trạng thái từng người nhận, nội dung trả lời và thời gian trả lời.",
          },
          {
            question: "Danh sách góp ý lọc theo tiêu chí nào?",
            answer:
              "Có thể lọc theo khoảng thời gian và phân loại góp ý, đồng thời tìm kiếm theo tên, số điện thoại hoặc nội dung.",
          },
        ],
      },
      {
        title: "Tài khoản và lịch trực",
        icon: "pi-calendar",
        items: [
          {
            question: "Đổi mật khẩu tài khoản thế nào?",
            answer:
              "Quản trị vào Quản lý tài khoản, chọn Chỉnh sửa, nhập mật khẩu mới tối thiểu 6 ký tự rồi bấm Cập nhật. Nếu không đổi mật khẩu thì để trống.",
          },
          {
            question: "Thêm lịch trực như thế nào?",
            answer:
              "Vào Quản lý lịch trực, bấm ngày cần phân công, chọn thành viên và bấm Thêm.",
          },
          {
            question: "Tôi có thể xóa người khỏi lịch trực không?",
            answer:
              "Có. Chọn ngày trên lịch, trong danh sách nhân viên trực bấm Xóa và xác nhận.",
          },
        ],
      },
    ];

    return { faqGroups };
  },
};
</script>
